/**
 * Витрина компонентов (§2.3) — служебная страница для визуальной приёмки.
 *
 * Каждый компонент показан во всех вариантах и состояниях, которые требует
 * спека, сгруппированно и с подписями. Тексты взяты из §12 дословно там, где
 * §12 их задаёт. Страница не входит в продукт: монтируется маршрутом
 * `/dev/components` или напрямую из `main.tsx` на время приёмки.
 *
 * Переключатели темы и плотности сверху ставят `data-theme` и `data-density` на
 * <html> — ровно те же атрибуты, что меню профиля в продукте (§2.1).
 */
import { useRef, useState } from 'react'
import type { ReactNode } from 'react'
import {
  Avatar,
  Badge,
  Button,
  ChannelSwitch,
  Checkbox,
  Chip,
  ConsequenceLine,
  EmptyState,
  FieldRow,
  IconArrowDown,
  IconArrowLeft,
  IconArrowRight,
  IconArrowUp,
  IconButton,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconClose,
  IconCloudOff,
  IconColumns,
  IconComment,
  IconConflict,
  IconCopy,
  IconDot,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconFilter,
  IconHistory,
  IconLink,
  IconPlus,
  IconProhibited,
  IconSearch,
  IconSort,
  IconTrash,
  IconWarning,
  Input,
  Menu,
  Modal,
  Popover,
  SegmentedControl,
  Select,
  Skeleton,
  Spinner,
  Textarea,
  TierBadge,
  Toast,
  ToastHost,
  Tooltip,
} from '../components'
import type { ChipState, SelectOption, ToastItem } from '../components'
import type { ChannelCode } from '@shared/types.ts'
import './ComponentsPreview.css'

function Section({
  title,
  note,
  children,
}: {
  title: string
  note?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="preview__section">
      <h2 className="preview__section-title t-h2">{title}</h2>
      {note ? <p className="preview__note t-meta">{note}</p> : null}
      {children}
    </section>
  )
}

function Case({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="preview__case">
      <span className="preview__case-label t-meta">{label}</span>
      {children}
    </div>
  )
}

/* Значения справочника `tier` и буквы-хоткеи из рисунка §2.3. */
const TIER_OPTIONS: SelectOption[] = [
  { value: 'A+', label: 'A+', badge: <TierBadge tier="A+" /> },
  { value: 'A', label: 'A', badge: <TierBadge tier="A" /> },
  { value: 'B', label: 'B', badge: <TierBadge tier="B" /> },
  { value: 'C', label: 'C', badge: <TierBadge tier="C" /> },
]

/* Справочник `seo_priority` (db/migrations/002_seed.sql), буквы — по §9.2. */
const SEO_OPTIONS: SelectOption[] = [
  { value: 'top1', label: 'TOP-1' },
  { value: 'top3', label: 'TOP-3' },
  { value: 'top10', label: 'TOP-10' },
  { value: 'no_promo', label: 'Не продвигать (чек-лист)' },
]

export function ComponentsPreview() {
  /* ── служебные переключатели витрины ── */
  const [theme, setTheme] = useState<'system' | 'light' | 'dark'>('system')
  const [density, setDensity] = useState<'normal' | 'roomy'>('normal')

  function applyTheme(next: 'system' | 'light' | 'dark') {
    setTheme(next)
    const root = document.documentElement
    if (next === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', next)
  }

  function applyDensity(next: 'normal' | 'roomy') {
    setDensity(next)
    const root = document.documentElement
    if (next === 'normal') root.removeAttribute('data-density')
    else root.setAttribute('data-density', 'roomy')
  }

  /* ── состояние демонстраций ── */
  const [tier, setTier] = useState<string | null>('A')
  const [tierCell, setTierCell] = useState<string | null>(null)
  const [seo, setSeo] = useState<string | null>(null)
  const [tierOpen, setTierOpen] = useState(false)
  const [tierCellOpen, setTierCellOpen] = useState(false)
  const [seoOpen, setSeoOpen] = useState(false)

  const [text, setText] = useState('/brands/aeg')
  const [note, setNote] = useState('уточнить у Лены')
  const [number, setNumber] = useState('42')
  const [wrongUrl, setWrongUrl] = useState('аск о .ру')

  const [onlyEmpty, setOnlyEmpty] = useState(true)
  const [overwrite, setOverwrite] = useState(false)

  const [chips, setChips] = useState<Record<string, ChipState>>({
    all: 'on',
    noUrl: 'off',
    categories: 'excluded',
  })

  const [channel, setChannel] = useState<ChannelCode>('retail')
  const [sort, setSort] = useState<'alphabet' | 'tier'>('alphabet')

  const [modalOpen, setModalOpen] = useState(false)
  const [irreversibleOpen, setIrreversibleOpen] = useState(false)

  const menuAnchor = useRef<HTMLButtonElement | null>(null)
  const [menuOpen, setMenuOpen] = useState(false)

  const popoverAnchor = useRef<HTMLButtonElement | null>(null)
  const [popoverOpen, setPopoverOpen] = useState(false)

  const [toasts, setToasts] = useState<ToastItem[]>([])
  const toastSeq = useRef(1)

  function pushToast(toast: Omit<ToastItem, 'id'>) {
    setToasts((list) => [...list, { ...toast, id: toastSeq.current++ }])
  }

  function dismissToast(id: number) {
    setToasts((list) => list.filter((item) => item.id !== id))
  }

  return (
    <div className="preview">
      <header className="preview__head">
        <h1 className="preview__title t-h1">Компоненты · §2.3</h1>
        <div className="preview__switches">
          <div className="preview__switch">
            <span className="t-label">Тема</span>
            <SegmentedControl<'system' | 'light' | 'dark'>
              value={theme}
              onChange={applyTheme}
              label="Тема"
              size="s"
              options={[
                { value: 'system', label: 'как в системе' },
                { value: 'light', label: 'светлая' },
                { value: 'dark', label: 'тёмная' },
              ]}
            />
          </div>
          <div className="preview__switch">
            <span className="t-label">Плотность</span>
            <SegmentedControl<'normal' | 'roomy'>
              value={density}
              onChange={applyDensity}
              label="Плотность"
              size="s"
              options={[
                { value: 'normal', label: 'Обычная' },
                { value: 'roomy', label: 'Просторная' },
              ]}
            />
          </div>
        </div>
      </header>

      <Section
        title="Button"
        note="Четыре варианта и четыре размера. Залитая зелёная кнопка на экране одна — primary. У disabled всегда есть текст-причина рядом (П6), поэтому кнопка остаётся в tab-порядке."
      >
        <div className="preview__grid">
          <Case label="primary · s / m / l / xl">
            <div className="preview__row">
              <Button variant="primary" size="s">Подтвердить</Button>
              <Button variant="primary" size="m">Подтвердить изменения (14)</Button>
              <Button variant="primary" size="l">Подтвердить изменения (14)</Button>
              <Button variant="primary" size="xl">Подтвердить и опубликовать (14)</Button>
            </div>
          </Case>
          <Case label="secondary / ghost / danger-ghost">
            <div className="preview__row">
              <Button variant="secondary">Назад к таблице</Button>
              <Button variant="ghost" iconBefore={<IconColumns size={16} />}>Колонки</Button>
              <Button variant="ghost" iconBefore={<IconFilter size={16} />}>Фильтры (2)</Button>
              <Button variant="danger-ghost">Отменить весь черновик</Button>
            </div>
          </Case>
          <Case label="loading · ширина не меняется">
            <div className="preview__row">
              <Button variant="primary" size="l" loading>Подтвердить изменения (14)</Button>
              <Button variant="secondary" loading>Обновить</Button>
            </div>
          </Case>
          <Case label="disabled + причина (П6)">
            <div className="preview__col">
              <Button variant="primary" size="l" disabled>Подтвердить изменения (14)</Button>
              <ConsequenceLine>Сначала решите 1 конфликт</ConsequenceLine>
              <Button variant="primary" size="l" disabled>Подтвердить изменения (12)</Button>
              <ConsequenceLine>Ждём 2 правки</ConsequenceLine>
            </div>
          </Case>
          <Case label="с иконками">
            <div className="preview__row">
              <Button variant="secondary" iconBefore={<IconPlus size={16} />}>Бренд</Button>
              <Button variant="ghost" iconBefore={<IconCopy size={16} />}>Ссылка на этот вид</Button>
              <Button variant="ghost" iconAfter={<IconChevronDown size={16} />}>А→Я</Button>
            </div>
          </Case>
        </div>
      </Section>

      <Section title="IconButton" note="Зона нажатия 32 px вне таблицы и 24 px внутри строки 28 px (§13). Доступное имя обязательно.">
        <div className="preview__grid">
          <Case label="ghost · s / m / l">
            <div className="preview__row">
              <IconButton size="s" label="Закрыть" icon={<IconClose size={16} />} />
              <IconButton size="m" label="История правок" icon={<IconHistory size={16} />} />
              <IconButton size="l" label="Поиск бренда" icon={<IconSearch size={20} />} />
            </div>
          </Case>
          <Case label="secondary / primary / danger-ghost">
            <div className="preview__row">
              <IconButton variant="secondary" label="Предыдущая пустая ячейка" icon={<IconChevronLeft size={16} />} />
              <IconButton variant="secondary" label="Следующая пустая ячейка" icon={<IconChevronRight size={16} />} />
              <IconButton variant="primary" label="Добавить бренд" icon={<IconPlus size={16} />} />
              <IconButton variant="danger-ghost" label="Убрать правку" icon={<IconTrash size={16} />} />
            </div>
          </Case>
          <Case label="в таблице (24×24) · нажатая · недоступная">
            <div className="preview__row">
              <IconButton inTable label="Комментарии" icon={<IconComment size={16} />} />
              <IconButton label="Фильтры" icon={<IconFilter size={16} />} aria-pressed />
              <IconButton label="Удалить бренд" icon={<IconTrash size={16} />} disabled />
            </div>
          </Case>
        </div>
      </Section>

      <Section
        title="Select · поповер справочника"
        note="Главный редактор справочных значений. Буква-хоткей написана справа и работает только внутри открытого поповера. ↑↓ — по строкам, Enter — выбрать, Esc — закрыть без правки, Tab — закрыть и уйти дальше."
      >
        <div className="preview__grid">
          <Case label="Приоритет (тир) · значение выбрано">
            <div className="preview__col">
              <Select
                label="Приоритет (тир)"
                options={TIER_OPTIONS}
                value={tier}
                onChange={setTier}
                open={tierOpen}
                onOpenChange={setTierOpen}
              />
            </div>
          </Case>
          <Case label="Приоритет SEO · пусто, длинная подпись">
            <div className="preview__col">
              <Select
                label="Приоритет SEO"
                options={SEO_OPTIONS}
                value={seo}
                onChange={setSeo}
                open={seoOpen}
                onOpenChange={setSeoOpen}
              />
            </div>
          </Case>
          <Case label="в ячейке таблицы · variant=plain, size=cell">
            <div className="preview__cell">
              <Select
                label="Приоритет (тир)"
                options={TIER_OPTIONS}
                value={tierCell}
                onChange={setTierCell}
                open={tierCellOpen}
                onOpenChange={setTierCellOpen}
                size="cell"
                variant="plain"
              />
            </div>
          </Case>
          <Case label="недоступен · невалиден">
            <div className="preview__col">
              <Select
                label="Приоритет (тир)"
                options={TIER_OPTIONS}
                value="B"
                onChange={() => undefined}
                open={false}
                onOpenChange={() => undefined}
                disabled
              />
              <Select
                label="Видимость на сайте"
                options={[
                  { value: 'yes', label: 'Да' },
                  { value: 'no', label: 'Нет' },
                ]}
                value={null}
                onChange={() => undefined}
                open={false}
                onOpenChange={() => undefined}
                allowEmpty={false}
                invalid
              />
              <ConsequenceLine tone="danger">Поле обязательно к заполнению</ConsequenceLine>
            </div>
          </Case>
        </div>
      </Section>

      <Section title="Input · Textarea" note="32 px в формах, 28 px в ячейке. Ошибка — строкой под полем, а не тултипом (П6).">
        <div className="preview__grid">
          <Case label="обычное · с иконкой · ссылка справа">
            <div className="preview__col">
              <Input
                aria-label="Поиск бренда"
                placeholder="Поиск бренда ( / )"
                iconBefore={<IconSearch size={16} />}
                value={text}
                onChange={(event) => setText(event.currentTarget.value)}
              />
              <Input
                aria-label="URL (Розница)"
                value="/brands/aeg"
                readOnly
                trailing={<IconExternalLink size={16} />}
                trailingIsLink
              />
            </div>
          </Case>
          <Case label="число · 28 px в ячейке">
            <div className="preview__col">
              <Input
                aria-label="Число"
                numeric
                inputMode="numeric"
                value={number}
                onChange={(event) => setNumber(event.currentTarget.value)}
              />
              <Input aria-label="Примечание в ячейке" size="cell" value="уточнить" readOnly />
            </div>
          </Case>
          <Case label="ошибка · недоступно">
            <div className="preview__col">
              <Input
                aria-label="URL (Розница)"
                value={wrongUrl}
                onChange={(event) => setWrongUrl(event.currentTarget.value)}
                error="Похоже, это не ссылка. Положить в «Примечание»?"
              />
              <Input aria-label="Доставка" value="" placeholder="пусто" disabled />
            </div>
          </Case>
          <Case label="Textarea · 3 строки, Enter фиксирует">
            <div className="preview__col">
              <Textarea
                aria-label="Примечание"
                value={note}
                commitOnEnter
                onCommit={() => pushToast({ kind: 'info', text: 'Примечание зафиксировано' })}
                onChange={(event) => setNote(event.currentTarget.value)}
                hint="Enter фиксирует, Shift+Enter переносит строку"
              />
            </div>
          </Case>
        </div>
      </Section>

      <Section title="Checkbox" note="Квадрат 16 px, зона нажатия — вся подпись. Частичное состояние нужно заголовку выделения в таблице.">
        <div className="preview__grid">
          <Case label="включён · выключен · частично · недоступен">
            <div className="preview__col">
              <Checkbox checked={onlyEmpty} onChange={setOnlyEmpty} label="Только пустые (9)" />
              <Checkbox checked={overwrite} onChange={setOverwrite} label="Перезаписать все (12)" hint="Заполненные значения тоже изменятся" />
              <Checkbox checked={false} indeterminate onChange={() => undefined} label="Выделить все 48 показанных" />
              <Checkbox checked={false} onChange={() => undefined} label="Архивные бренды" hint="Нет права архивировать бренды" disabled />
            </div>
          </Case>
        </div>
      </Section>

      <Section
        title="Chip · фильтр-чип со счётчиком"
        note="Выключен · включён · исключён (Alt+клик, знак «−» впереди). Клик заменяет выбор, Shift+клик добавляет, Alt+клик исключает."
      >
        <div className="preview__row">
          <Chip
            label="Все бренды"
            count={230}
            state={chips.all}
            onToggle={() => setChips((state) => ({ ...state, all: state.all === 'on' ? 'off' : 'on' }))}
          />
          <Chip
            label="Без URL"
            count={52}
            state={chips.noUrl}
            onToggle={(intent) =>
              setChips((state) => ({
                ...state,
                noUrl: intent === 'exclude' ? 'excluded' : state.noUrl === 'on' ? 'off' : 'on',
              }))
            }
          />
          <Chip
            label="Категории не уточнены"
            count={127}
            icon={<IconWarning size={16} />}
            state={chips.categories}
            onToggle={(intent) =>
              setChips((state) => ({
                ...state,
                categories: intent === 'exclude' ? 'excluded' : state.categories === 'on' ? 'off' : 'on',
              }))
            }
          />
          <Chip label="Скрыт" count={129} icon={<IconProhibited size={16} />} />
          <Chip label="Комментарии" count={73} icon={<IconComment size={16} />} />
          <Chip label="Изменён 7 дн" count={23} />
          <Chip label="Расхождения каналов" count={54} disabled />
        </div>
      </Section>

      <Section
        title="TierBadge"
        note="Убывающий визуальный вес: A+ залитый, A светлая заливка с рамкой, B серая плашка, C только текст с пунктирной рамкой. Пусто — пунктир и «—»: это задача, а не «тира нет по смыслу» (П3)."
      >
        <div className="preview__row">
          <TierBadge tier="A+" />
          <TierBadge tier="A" />
          <TierBadge tier="B" />
          <TierBadge tier="C" />
          <TierBadge tier={null} />
        </div>
      </Section>

      <Section title="Badge" note="Счётчики и короткие статусы. Оранжевый — только черновик и только подложкой с рамкой (§2.1).">
        <div className="preview__row">
          <Badge>46 категорий</Badge>
          <Badge variant="accent">представление</Badge>
          <Badge variant="success">опубликовано</Badge>
          <Badge variant="danger">конфликт</Badge>
          <Badge variant="draft" pill dot>Черновик · 14</Badge>
          <Badge variant="fresh">новое с прошлого визита</Badge>
        </div>
      </Section>

      <Section title="ChannelSwitch · SegmentedControl" note="Две половины по 92 px и число брендов канала рядом: у каждой цифры написана её вселенная (П7).">
        <div className="preview__grid">
          <Case label="канал">
            <ChannelSwitch
              value={channel}
              onChange={setChannel}
              caption={channel === 'retail' ? '230 брендов' : '186 брендов'}
            />
          </Case>
          <Case label="сортировка">
            <SegmentedControl<'alphabet' | 'tier'>
              value={sort}
              onChange={setSort}
              label="Сортировка"
              options={[
                { value: 'alphabet', label: 'А→Я' },
                { value: 'tier', label: 'По тиру' },
              ]}
            />
          </Case>
        </div>
      </Section>

      <Section title="Tooltip" note="Задержка 400 мс, исчезновение сразу, Esc скрывает. Показывается и по фокусу с клавиатуры. Ничего незаменимого в тултипе не живёт (П5).">
        <div className="preview__row">
          <Tooltip content="было: B · 11:42 · видите только вы">
            <Button variant="secondary">Черновая ячейка</Button>
          </Tooltip>
          <Tooltip content="Обновления не приходят с 16:41. Проверьте связь">
            <IconButton label="Состояние связи" icon={<IconCloudOff size={16} />} />
          </Tooltip>
          <Tooltip content="После обновления эта строка уйдёт из текущего фильтра">
            <IconButton label="Строка уйдёт из фильтра" icon={<IconArrowDown size={16} />} />
          </Tooltip>
        </div>
      </Section>

      <Section
        title="Modal"
        note="Только для необратимого (П6). Фокус запирается внутри, Esc = «Отмена», Enter — главное действие. Для необратимого действия подложка и Esc не закрывают: выход только кнопкой."
      >
        <div className="preview__row">
          <Button variant="secondary" onClick={() => setModalOpen(true)}>
            Обычная модалка (s)
          </Button>
          <Button variant="danger-ghost" onClick={() => setIrreversibleOpen(true)}>
            Необратимое действие (m)
          </Button>
        </div>

        <Modal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          title="Развернуть все бренды"
          size="s"
          onConfirm={() => setModalOpen(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setModalOpen(false)}>Отмена</Button>
              <Button variant="primary" onClick={() => setModalOpen(false)}>Развернуть</Button>
            </>
          }
        >
          <p className="t-body">Развернуть 48 брендов — это 173 строки. Продолжить?</p>
        </Modal>

        <Modal
          open={irreversibleOpen}
          onClose={() => setIrreversibleOpen(false)}
          title="Отменить весь черновик"
          size="m"
          dismissible={false}
          footerNote="Действие необратимо"
          footer={
            <>
              <Button variant="secondary" onClick={() => setIrreversibleOpen(false)}>Отмена</Button>
              <Button
                variant="danger-ghost"
                onClick={() => {
                  setIrreversibleOpen(false)
                  pushToast({ kind: 'info', text: 'Черновик отменён · 14 правок' })
                }}
              >
                Отменить весь черновик
              </Button>
            </>
          }
        >
          <p className="t-body">
            14 правок по 9 брендам исчезнут. Вернуть их будет нельзя — история пакетов хранит только
            подтверждённое.
          </p>
        </Modal>
      </Section>

      <Section title="Menu" note="Строка 32 px, разделитель с воздухом, опасный пункт красным. Недоступный по правам пункт остаётся на месте и объясняет причину (П4).">
        <div className="preview__row">
          <Button
            ref={menuAnchor}
            variant="ghost"
            iconAfter={<IconChevronDown size={16} />}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            Действия с брендом
          </Button>
          <Menu
            open={menuOpen}
            anchor={menuAnchor.current}
            onClose={() => setMenuOpen(false)}
            onSelect={(id) => pushToast({ kind: 'info', text: `Пункт меню: ${id}` })}
            label="Действия с брендом"
            items={[
              { id: 'open', label: 'Открыть карточку', icon: <IconExternalLink size={16} /> },
              { id: 'link', label: 'Ссылка на бренд', icon: <IconCopy size={16} /> },
              { id: 'history', label: 'История правок', icon: <IconHistory size={16} /> },
              { id: 'b2b', label: 'Добавить в Опт', icon: <IconPlus size={16} />, separatorBefore: true },
              {
                id: 'archive',
                label: 'Архивировать бренд',
                icon: <IconTrash size={16} />,
                danger: true,
                disabled: true,
                reason: 'Доступно коммерческому директору и собственнику',
              },
            ]}
          />
        </div>
      </Section>

      <Section title="Popover · базовый примитив" note="Позиционирование относительно триггера с переворотом при нехватке места, Esc и клик вне закрывают, фокус возвращается на триггер.">
        <div className="preview__row">
          <Button
            ref={popoverAnchor}
            variant="secondary"
            aria-expanded={popoverOpen}
            onClick={() => setPopoverOpen((open) => !open)}
          >
            Открыть поповер
          </Button>
          <Popover
            open={popoverOpen}
            anchor={popoverAnchor.current}
            onClose={() => setPopoverOpen(false)}
            role="dialog"
            minWidth={320}
            focusOnOpen
          >
            <div className="preview__surface">
              <p className="t-body">
                Правка сохранена в вашем черновике. Остальные её пока не видят. Когда закончите —
                нажмите «Подтвердить изменения» внизу.
              </p>
              <div className="preview__row">
                <Button variant="secondary" onClick={() => setPopoverOpen(false)}>Понятно</Button>
              </div>
            </div>
          </Popover>
        </div>
      </Section>

      <Section title="Toast · ToastHost" note="Низ по центру, максимум три стопкой, ховер останавливает таймер. Успех 4 с, нейтральный 6 с, ошибка 8 с; у тоста с действием — 8 с.">
        <div className="preview__row">
          <Button
            variant="secondary"
            onClick={() =>
              pushToast({
                kind: 'success',
                text: 'Пакет #130 опубликован · 14 правок · отправлено в Telegram',
                action: { label: 'Посмотреть', onClick: () => undefined },
              })
            }
          >
            Успех с действием
          </Button>
          <Button
            variant="secondary"
            onClick={() => pushToast({ kind: 'info', text: 'Ссылка скопирована' })}
          >
            Нейтральный
          </Button>
          <Button
            variant="secondary"
            onClick={() =>
              pushToast({
                kind: 'error',
                text: 'Не удалось подтвердить пакет: сервер недоступен. Черновик на месте, попробуйте ещё раз',
              })
            }
          >
            Ошибка
          </Button>
        </div>

        <div className="preview__grid">
          <Case label="статично, вне хоста — чтобы видеть все три варианта сразу">
            <div className="preview__col" style={{ width: 'auto' }}>
              <Toast
                toast={{ id: -1, kind: 'info', text: 'Отменено: Asko · Приоритет (тир)', action: { label: 'Вернуть', onClick: () => undefined } }}
                onDismiss={() => undefined}
                autoDismiss={false}
              />
              <Toast
                toast={{ id: -2, kind: 'success', text: 'Создан черновик отката пакета #128 · 31 правка' }}
                onDismiss={() => undefined}
                autoDismiss={false}
              />
              <Toast
                toast={{ id: -3, kind: 'error', text: 'Слишком много запросов. Подождите несколько секунд' }}
                onDismiss={() => undefined}
                autoDismiss={false}
              />
            </div>
          </Case>
        </div>
      </Section>

      <Section title="EmptyState" note="Блок 420 px по центру: заголовок, объяснение с числом и выход одной кнопкой (§11.2). Иллюстраций нет.">
        <div className="preview__surface">
          <EmptyState
            title="Все приоритеты SEO проставлены"
            text="В Рознице не осталось брендов без приоритета SEO. В Опте — 98."
            actions={
              <>
                <Button variant="secondary">Посмотреть Опт</Button>
                <Button variant="ghost">Все бренды</Button>
              </>
            }
          />
        </div>
        <div className="preview__surface" style={{ marginTop: 'var(--sp-6)' }}>
          <EmptyState
            title="Под эти фильтры ничего не попало"
            text={
              <>
                Активны: Тир A+, A · Без URL
              </>
            }
            actions={<Button variant="secondary">Очистить всё</Button>}
          />
        </div>
      </Section>

      <Section title="Skeleton" note="12 строк по 28 px, полосы 60 % ширины колонки, без переливов. При prefers-reduced-motion выглядит так же.">
        <div className="preview__surface" style={{ padding: 0 }}>
          <Skeleton rows={6} />
        </div>
      </Section>

      <Section title="Avatar" note="18 px в таблице и ленте, 24 px в карточке и шапке, 32 px в профиле. Цвет детерминированный. Системный автор импорта — «ИМ».">
        <div className="preview__row">
          <Avatar name="Богдан Царелунга" />
          <Avatar name="Богдан Царелунга" size={24} />
          <Avatar name="Богдан Царелунга" size={32} />
          <Avatar name="Лена Усова" size={24} />
          <Avatar name="Кирилл Горчатов" size={24} />
          <Avatar name="Александр Шеленко" size={24} />
          <Avatar name="Импорт из Google-таблицы" size={24} system />
        </div>
      </Section>

      <Section title="Spinner" note="Единственная вращающаяся анимация. 14 px внутри кнопки, 16 и 20 px в блоках.">
        <div className="preview__row">
          <Spinner />
          <Spinner size={16} />
          <Spinner size={20} label="Подтверждаем пакет" />
        </div>
      </Section>

      <Section title="FieldRow · ConsequenceLine" note="Подпись, контрол и серая строка-последствие под ним. Последствие объясняется строкой у контрола, а не диалогом (П6).">
        <div className="preview__grid">
          <Case label="подпись сверху">
            <div className="preview__col">
              <FieldRow
                label="Видимость на сайте"
                group
                consequence={<ConsequenceLine>Бренд исчезнет со страниц сайта после публикации</ConsequenceLine>}
              >
                <Select
                  label="Видимость на сайте"
                  options={[
                    { value: 'yes', label: 'Да' },
                    { value: 'no', label: 'Нет' },
                  ]}
                  value="no"
                  onChange={() => undefined}
                  open={false}
                  onOpenChange={() => undefined}
                />
              </FieldRow>
            </div>
          </Case>
          <Case label="подпись слева · «моё» поле · ошибка">
            <div className="preview__col" style={{ width: 460 }}>
              <FieldRow
                label="Название бренда"
                htmlFor="preview-brand-name"
                layout="inline"
                mine
                consequence={<ConsequenceLine>Название изменится во всех каналах, в истории и в уведомлениях</ConsequenceLine>}
              >
                <Input id="preview-brand-name" defaultValue="Asko" />
              </FieldRow>
              <FieldRow
                label="Приоритет SEO"
                htmlFor="preview-seo"
                layout="inline"
                error="Поле обязательно к заполнению"
              >
                <Input id="preview-seo" defaultValue="" invalid />
              </FieldRow>
            </div>
          </Case>
        </div>
      </Section>

      <Section title="Иконки" note="Сетка 20×20, рендер 16 и 20 px, currentColor, обводка 1.5 px.">
        <div className="preview__icons">
          {([
            ['ChevronDown', <IconChevronDown size={20} key="a" />],
            ['ChevronUp', <IconChevronUp size={20} key="b" />],
            ['ChevronLeft', <IconChevronLeft size={20} key="c" />],
            ['ChevronRight', <IconChevronRight size={20} key="d" />],
            ['ArrowUp', <IconArrowUp size={20} key="e" />],
            ['ArrowDown', <IconArrowDown size={20} key="f" />],
            ['ArrowLeft', <IconArrowLeft size={20} key="g" />],
            ['ArrowRight', <IconArrowRight size={20} key="h" />],
            ['Close', <IconClose size={20} key="i" />],
            ['Check', <IconCheck size={20} key="j" />],
            ['Plus', <IconPlus size={20} key="k" />],
            ['Trash', <IconTrash size={20} key="l" />],
            ['Search', <IconSearch size={20} key="m" />],
            ['Filter', <IconFilter size={20} key="n" />],
            ['Columns', <IconColumns size={20} key="o" />],
            ['Sort', <IconSort size={20} key="p" />],
            ['Comment', <IconComment size={20} key="q" />],
            ['History', <IconHistory size={20} key="r" />],
            ['Link', <IconLink size={20} key="s" />],
            ['ExternalLink', <IconExternalLink size={20} key="t" />],
            ['Copy', <IconCopy size={20} key="u" />],
            ['Eye', <IconEye size={20} key="v" />],
            ['EyeOff', <IconEyeOff size={20} key="w" />],
            ['Prohibited', <IconProhibited size={20} key="x" />],
            ['Warning', <IconWarning size={20} key="y" />],
            ['Conflict', <IconConflict size={20} key="z" />],
            ['CloudOff', <IconCloudOff size={20} key="aa" />],
            ['Dot', <IconDot size={20} key="ab" />],
          ] as Array<[string, ReactNode]>).map(([name, icon]) => (
            <span className="preview__icon t-meta" key={name}>
              {icon}
              {name}
            </span>
          ))}
        </div>
      </Section>

      <ToastHost toasts={toasts} onDismiss={dismissToast} />
    </div>
  )
}

export default ComponentsPreview
